/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import ReservationCheckoutPlanRow
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/CheckoutPlanner/components/ReservationCheckoutPlanRow";
import {CheckIcon} from "@heroicons/react/24/solid";
import {ShoppingCartItemPaymentType} from "types/enums/ShoppingCartItemPaymentType";
import UiButton from "pages/Front/components/Ui/components/UiButton";
import ArticlesAction from "store/articles/ArticlesAction";
import {ReservationCheckoutPlan} from "types/entities/ReservationCheckoutPlan";
import {toast} from "react-toastify";
import {ArticleGroup} from "types/entities/ArticleGroup";
import {useTypedSelector} from "helpers/reducers";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {Reservation} from "types/entities/Reservation";
import SellerAction from "store/seller/SellerAction";
import {useHistory} from "react-router";

interface CheckoutPlannerProps {
    reservation: Reservation;
}

const CheckoutPlanner = (props: CheckoutPlannerProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    // const reservation = useTypedSelector(state => state.calendar.selectedReservation);
    const articleGroups = useTypedSelector(state => state.articlesState.articleGroups);
    const [reservationCheckoutPlan, setReservationPlans] = useState<ReservationCheckoutPlan[]>(props.reservation.extra.reservation_checkout_plan);
    const filteredArticleIds = [1002, 1003, 1004, 1007, 1009, 4000, 4001];

    const matchingCheckoutPlan = (articleGroup: ArticleGroup) => {
        const defaultCheckoutPlan: ReservationCheckoutPlan = {
            article_group_id: articleGroup.id,
            payment_type: ShoppingCartItemPaymentType.Checkout,
            is_active: false,
        };
        return reservationCheckoutPlan.find(plan => plan.article_group_id === articleGroup.id) || defaultCheckoutPlan;
    };

    const updatePlanHandler = (newPlan: ReservationCheckoutPlan) => {
        const planIndex = reservationCheckoutPlan.findIndex(plan => plan.article_group_id === newPlan.article_group_id);
        const newPlans = [...reservationCheckoutPlan];
        if (planIndex === -1) {
            newPlans.push(newPlan);
        } else {
            newPlans[planIndex] = newPlan;
        }
        setReservationPlans(newPlans);
    };

    const savePlansHandler = () => {
        dispatch(SellerAction.setReservationCheckoutPlans(reservationCheckoutPlan));
        dispatch(SellerAction.fetchSetReservationCheckoutPlan(props.reservation.id, reservationCheckoutPlan))
            .then(r => {
                toast.success(t("Reservation checkout plan has been created."));
            })
            .catch(err => {
                toast.error(t("Reservation checkout plan failed."));
                if (err?.status && err?.status === 401) {
                    history.replace("/booking/seller/login");
                }
            });
    };

    const onUpdatePlanAllHandle = (name: keyof ReservationCheckoutPlan, value: ShoppingCartItemPaymentType | boolean) => {
        const mergedPlans = articleGroups && articleGroups.map(articleGroup => {
            const existingPlan = reservationCheckoutPlan.find(
                plan => plan.article_group_id === articleGroup.id,
            );
            return existingPlan ? {...existingPlan} : matchingCheckoutPlan(articleGroup);
        });
        const updatePlan = (plan: ReservationCheckoutPlan): ReservationCheckoutPlan => ({
            ...plan,
            [name]: value,
        });
        const updatedPlans = mergedPlans && mergedPlans.map(updatePlan);
        updatedPlans && setReservationPlans(updatedPlans);
    };

    useEffect(() => {
        if (!articleGroups || articleGroups.length === 0) {
            dispatch(ArticlesAction.fetchArticleGroups())
                .then(r => {})
                .catch(err => {
                    if (err?.status && err?.status === 401) {
                        history.replace("/booking/seller/login");
                    }
                });
        }
    }, [articleGroups]);

    return (
        <div className={"CheckoutPlanner flex flex-col w-full h-full relative"}>
            <div
                className={"flex flex-row justify-between gap-x-2 p-2 xl:p-4 pt-0 xl:pt-0 pl-0 xl:pl-0 shadow-sm border-b border-b-gray-400 sticky top-0 bg-gray-50 z-10"}>
                <div className={"flex items-center"}>
                    <button
                        className={"flex items-center bg-dark-800 hover:bg-opacity-80 text-white font-bold rounded px-2 py-1 justify-center duration-150"}
                        onClick={savePlansHandler}
                    >
                        <CheckIcon className={"w-4 h-4 mr-2"} />
                        <span>{t("Save plan")}</span>
                    </button>
                </div>
                <div className={"flex flex-col"}>
                    <span className={"font-bold mb-1 "}>{t("All payments change to")}:</span>
                    <div className={"flex items-center space-x-2"}>
                        <UiButton
                            onClick={() => onUpdatePlanAllHandle("payment_type", ShoppingCartItemPaymentType.Checkout)}
                        >
                            <span>{t("Checkout")}</span>
                        </UiButton>
                        <UiButton
                            onClick={() => onUpdatePlanAllHandle("payment_type", ShoppingCartItemPaymentType.Invoice)}
                        >
                            <span>{t("Invoice")}</span>
                        </UiButton>
                    </div>
                </div>
                <div className={"justify-end flex flex-col"}>
                    <span className={"font-bold mb-1 text-end"}>{t("All status change to")}:</span>
                    <div className={"flex items-center justify-end space-x-2"}>
                        <UiButton
                            onClick={() => onUpdatePlanAllHandle("is_active", false)}
                        >
                            <span>{t("Inactive")}</span>
                        </UiButton>
                        <UiButton
                            onClick={() => onUpdatePlanAllHandle("is_active", true)}
                        >
                            <span>{t("Active")}</span>
                        </UiButton>
                    </div>
                </div>
            </div>
            {/*<div className={"flex flex-col h-auto overflow-hidden w-full pb-2 px-2 xl:pb-4 xl:px-4"}>*/}
            <div className={"flex flex-col h-auto w-full pb-2 pr-2 xl:pb-4 xl:pr-4"}>
                {/*<div className={"ReservationCheckoutPlanItems space-y-4 overflow-y-auto h-full pt-2 xl:pt-4"}>*/}
                <div className={"ReservationCheckoutPlanItems space-y-4 h-full pt-2 xl:pt-4"}>
                    {articleGroups &&
                        articleGroups
                            .filter(articleGroup => !filteredArticleIds.includes(articleGroup.id))
                            .map((articleGroup, index) => {
                            return <ReservationCheckoutPlanRow key={index} plan={matchingCheckoutPlan(articleGroup)}
                                                               onChange={updatePlanHandler} />;
                        })}
                </div>
            </div>
        </div>
    );
};

export default CheckoutPlanner;